import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Crown,
  X,
  Check,
  Users,
  HardDrive,
  Volume2,
  Calendar,
  Zap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import MsStoreIAPModal from './MsStoreIAPModal';

const PlusModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation('shared');

  const FEATURES = [
    { icon: HardDrive, text: t('plusModal.features.upload') },
    { icon: Users, text: t('plusModal.features.collabEdit') },
    { icon: Users, text: t('plusModal.features.groups') },
    { icon: Volume2, text: t('plusModal.features.sounds') },
    { icon: Calendar, text: t('plusModal.features.calendar') },
    { icon: Zap, text: t('plusModal.features.support') },
  ];

  if (typeof document === 'undefined') return null;

  // Microsoft Store build — real IAP purchase flow
  if (window.api?.isWindowsStore) {
    return <MsStoreIAPModal isOpen={isOpen} onClose={onClose} />;
  }

  // Web / NSIS / Dev — features + Store-only availability (no checkout)
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[99000] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            className="relative w-full max-w-lg bg-[#0d1424] border border-white/[0.08] rounded-2xl shadow-[0_32px_80px_rgba(0,0,0,0.7)] overflow-hidden"
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ duration: 0.2 }}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1.5 rounded-lg text-[#4d6a8a] hover:text-[#c8d8eb] hover:bg-white/[0.06] transition-colors z-10"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="px-6 pt-6 pb-4 text-center border-b border-white/[0.06]">
              <div className="flex justify-center mb-3">
                <div className="w-12 h-12 rounded-2xl bg-amber-500/15 flex items-center justify-center">
                  <Crown className="w-6 h-6 text-amber-400" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-white mb-1">{t('plusModal.title')}</h2>
              <p className="text-sm text-[#4d6a8a]">
                {t('plusModal.subtitle')}
              </p>
            </div>

            <div className="px-6 py-4 border-b border-white/[0.06]">
              <div className="space-y-2.5">
                {FEATURES.map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#3d8ef0]/15 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3.5 h-3.5 text-[#3d8ef0]" />
                    </div>
                    <span className="text-sm text-[#c8d8eb]">{text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="px-6 py-5">
              <p className="text-center text-sm text-[#8aaccc] leading-relaxed">
                {t('plusModal.storeOnlyAvailability')}
              </p>
              <button
                onClick={onClose}
                className="mt-4 w-full py-3 rounded-xl bg-[#161f31] hover:bg-[#1a2640] border border-white/[0.07] text-white text-sm font-semibold transition-colors"
              >
                {t('plusModal.gotIt')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default PlusModal;
